// src/components/staff/StaffCard.tsx
import React, { useState } from 'react';
import { Mail, CheckCircle2, AlertCircle, Shield, Trash2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUsers } from '@/contexts/UsersContext';
import { toast } from 'sonner';
import ConfirmDialog from '../ui/ConfirmDialog';

interface StaffMember {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: 'Admin' | 'Manager' | 'Staff';
    isEmailVerified: boolean;
    avatarColor: string;
    avatar: string | null;
    isInvite?: boolean;
}

interface StaffCardProps {
    staff: StaffMember;
    onClick: () => void;
}


const StaffCard: React.FC<StaffCardProps> = ({ staff, onClick }) => {
    const { t } = useTranslation();
    const { deleteInvite } = useUsers();
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const isInvite = staff.isInvite === true;


    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        // Extract token from id "invite-{token}"
        const token = staff.id.replace('invite-', '');

        const result = await deleteInvite(token);

        if (result.success) {
            toast.success(t('staff.details.toast.invite_deleted', { defaultValue: t('staff.details.invite_delete.success') }));
        } else {
            toast.error(t('staff.details.toast.invite_delete_error', { defaultValue: t('staff.details.invite_delete.error') }));
        }
        setIsDeleting(false);
        setShowDeleteConfirm(false);
    };

    return (
        <>
            <div
                onClick={onClick}
                className={`group bg-white rounded-3xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-gray-200/50 hover:border-gray-300 transition-all cursor-pointer relative overflow-hidden ${isInvite ? 'opacity-95' : ''
                    }`}
            >
                <div className="flex items-start justify-between mb-6">

                    {/* AVATAR SECTION — iniziali su sfondo colorato (stile Gmail) */}
                    <div
                        className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold uppercase group-hover:scale-110 transition-transform duration-300 ${staff.avatarColor}`}
                    >
                        {staff.firstName[0]}
                        {staff.lastName[0]}
                    </div>

                    <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2">
                            {isInvite && (
                                <div className="px-2.5 py-1 rounded-full text-xs font-bold border bg-gray-100 text-gray-500 border-gray-200">
                                    {t('staff.card.invite')}
                                </div>
                            )}

                            {/* Delete button for invites */}
                            {isInvite && (
                                <button
                                    onClick={handleDeleteClick}
                                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                    disabled={isDeleting}
                                    title={t('common.delete')}
                                >
                                    {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-clip-600 transition-colors">
                        {staff.firstName} {staff.lastName}
                    </h3>

                    <div className="flex items-center gap-2 mt-1">
                        <span className="flex items-center gap-1 text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            <Shield size={10} /> {staff.role}
                        </span>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-50 text-sm text-gray-500">
                        <div className="flex items-center gap-2 truncate">
                            <Mail size={14} className="flex-shrink-0" />
                            <span className="truncate">{staff.email}</span>
                            {staff.isEmailVerified ? (
                                <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" />
                            ) : (
                                <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                            )}
                        </div>

                        {!staff.isEmailVerified && isInvite && (
                            <p className="mt-1 text-[11px] font-semibold text-amber-600">
                                {t('staff.card.invite_pending')}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onCancel={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
                title={t('staff.details.invite_delete.title')}
                description={t('staff.details.invite_delete.description')}
                confirmLabel={t('common.delete')}
                variant="danger"
                loading={isDeleting}
            />
        </>
    );
};

export default StaffCard;